import React from 'react';
import * as Y from 'yjs';
import { TiptapCollabProvider } from '@tiptap-pro/provider';

import SessionStore from 'stores/Session';

import Logger from 'utils/logger';
import { getColorFromInitials } from 'components/Avatar/utils';

import { EDITOR_STATUS } from '../types';
import { EDITOR_CONSTANTS } from '../constants';

interface UseCollaborationProviderProps {
  uuid: string;
  session: SessionStore;
  tiptapToken?: string;
  getTiptapToken?: () => Promise<string>;
  organisation?: string;
  editable?: boolean;
}

export const useCollaborationProvider = ({
  uuid,
  session,
  tiptapToken,
  getTiptapToken,
  organisation,
  editable = true,
}: UseCollaborationProviderProps): {
  ydoc: Y.Doc;
  provider: TiptapCollabProvider;
  status: string;
} => {
  const [status, setStatus] = React.useState(EDITOR_STATUS.DISCONNECTED);

  const userUuid = session.user?.uuid;
  const orgUuid = session.user?.organisation.uuid;

  const { ydoc, provider } = React.useMemo(() => {
    const currentYdoc = new Y.Doc();

    if (!getTiptapToken && !tiptapToken) {
      throw new Error('No token provided');
    }

    const currentProvider = new TiptapCollabProvider({
      name: `${organisation || orgUuid}/${uuid}`,
      appId: EDITOR_CONSTANTS.ENV.TIP_TAP_APP_ID,
      token: getTiptapToken || (tiptapToken as string),
      document: currentYdoc,
    });

    // Set user awareness data for PermanentUserData extension
    const userName = session.user?.name || 'Anonymous';
    const nameParts = userName.trim().toUpperCase().split(' ');
    const firstName = nameParts[0]?.charAt(0) || 'A';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : firstName;
    const userColor = getColorFromInitials(firstName, lastName) || '#b616e4';

    currentProvider.setAwarenessField('user', {
      name: userName,
      color: userColor,
      clientId: userUuid || 'anonymous',
    });

    return { ydoc: currentYdoc, provider: currentProvider };
  }, [uuid, userUuid, orgUuid, tiptapToken, getTiptapToken, organisation, session.user?.name]);

  React.useEffect(() => {
    const handleSynced = (): void => setStatus(EDITOR_STATUS.SYNCED);
    const handleAuthFailed = ({ reason }: { reason: string }): void => {
      Logger.warn('Authentication failed:', reason);
      setStatus(EDITOR_STATUS.ERROR);
    };
    const handleError = ({ error }: { error: string }): void => {
      Logger.error('Error:', error);
      setStatus(EDITOR_STATUS.ERROR);
    };
    const handleStatus = ({ status: providerStatus }: { status: string }): void => {
      if (providerStatus === EDITOR_STATUS.CONNECTED) {
        setStatus(EDITOR_STATUS.CONNECTED);
        // Disable automatic versioning - versions are created manually via Create Version button
        if (editable) {
          const config = provider.configuration.document.getMap<number>('__tiptapcollab__config');
          if (config.get('intervalSeconds') !== 0) {
            config.set('intervalSeconds', 0);
          }
        }
      } else if (providerStatus === EDITOR_STATUS.DISCONNECTED) {
        setStatus(EDITOR_STATUS.DISCONNECTED);
      } else if (providerStatus === EDITOR_STATUS.ERROR) {
        setStatus(EDITOR_STATUS.ERROR);
      }
    };

    provider.on('synced', handleSynced);
    provider.on('authenticationFailed', handleAuthFailed);
    provider.on('error', handleError);
    provider.on('status', handleStatus);

    return () => {
      provider.off('synced', handleSynced);
      provider.off('authenticationFailed', handleAuthFailed);
      provider.off('error', handleError);
      provider.off('status', handleStatus);
      provider.destroy();
      ydoc.destroy();
    };
  }, [provider, editable]);

  return { ydoc, provider, status };
};
